import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import '../components/Signup/Signup.css';
import { useAgents } from '../hooks/useApiHooks';
import { AddorEditAgent } from '../components/CustomsAgent/AddorEditAgent';
import { DeleteAgent } from '../components/CustomsAgent/DeleteAgent';
import { AgentsTable } from '../components/CustomsAgent/AgentsTable';
import { ChevronDown, ArrowUpDown, Plus, Search } from 'lucide-react';
import toast from 'react-hot-toast';
import { useLanguage, getCustomsAgentManagementTranslation } from '../hooks/useLanguage';
import { CardStat } from '../components/CardStat';

function CustomsAgent() {
  const dispatch = useDispatch();
  const agents = useSelector((state) => state.agents || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSpanish] = useLanguage();

  useAgents();

  const [formData, setFormData] = useState({
    nombre: '',
    nif: '',
    direccion: '',
    localidad: '',
    pais: '',
    eori: '',
  });
  const [isEditing, setIsEditing] = useState(false);
  const [currentAgentId, setCurrentAgentId] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [agentToDelete, setAgentToDelete] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [sortBy, setSortBy] = useState('name-asc');
  const [isSortOpen, setIsSortOpen] = useState(false);

  const sortOptions = [
    { value: 'name-asc', label: getCustomsAgentManagementTranslation('nameAZ', isSpanish) },
    { value: 'name-desc', label: getCustomsAgentManagementTranslation('nameZA', isSpanish) },
    { value: 'date-desc', label: getCustomsAgentManagementTranslation('newestFirst', isSpanish) },
    { value: 'date-asc', label: getCustomsAgentManagementTranslation('oldestFirst', isSpanish) }
  ];

  const filteredAgents = agents.filter(agent => 
    agent.nombre.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.nif?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.eori?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    agent.pais?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const sortedAgents = [...filteredAgents].sort((a, b) => {
    switch (sortBy) {
      case 'name-asc':
        return a.nombre.localeCompare(b.nombre);
      case 'name-desc':
        return b.nombre.localeCompare(a.nombre);
      case 'date-asc':
        return new Date(a.date_created) - new Date(b.date_created);
      case 'date-desc':
        return new Date(b.date_created) - new Date(a.date_created);
      default:
        return 0;
    }
  });

  const getSortLabel = () => {
    return sortOptions.find(option => option.value === sortBy)?.label || getCustomsAgentManagementTranslation('sortBy', isSpanish);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleAdd = () => {
    setIsEditing(false);
    setFormData({
      nombre: '',
      nif: '',
      direccion: '',
      localidad: '',
      pais: '',
      eori: '',
    });
    setIsModalOpen(true);
  };

  const handleEdit = (agent) => {
    setIsEditing(true);
    setCurrentAgentId(agent.id);
    setFormData({
      nombre: agent.nombre || '',
      nif: agent.nif || '',
      direccion: agent.direccion || '',
      localidad: agent.localidad || '',
      pais: agent.pais || '',
      eori: agent.eori || '',
    });
    setIsModalOpen(true);
  };

  const openDeleteModal = (agent) => {
    setAgentToDelete(agent);
    setIsDeleteModalOpen(true);
  };

  const closeDeleteModal = () => {
    setIsDeleteModalOpen(false);
    setAgentToDelete(null);
  };

  const confirmDelete = async () => {
    if (!agentToDelete) return;

    try {
      setIsLoading(true);
      const response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/agents/${agentToDelete.id}`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
          'Content-Type': 'application/json',
        },
      });

      if (!response.ok) {
        throw new Error('Failed to delete agent');
      }

      dispatch({ type: 'DELETE_AGENT', payload: agentToDelete.id });
      toast.success(getCustomsAgentManagementTranslation('successDeleteAgent', isSpanish));
      closeDeleteModal();

    } catch (err) {
      console.error('Error deleting agent:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    const apiData = {
      nombre: formData.nombre,
      nif: formData.nif,
      eori: formData.eori,
      direccion: formData.direccion,
      localidad: formData.localidad,
      pais: formData.pais,
    };

    try {
      let response;

      if (isEditing) {
        response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/agents/${currentAgentId}`, {
          method: 'PATCH',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });
        
        toast.success(getCustomsAgentManagementTranslation('successUpdateAgent', isSpanish));

      } else {
        response = await fetch(`https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/agents`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
          },
          body: JSON.stringify(apiData),
        });
        
        toast.success(getCustomsAgentManagementTranslation('successCreateAgent', isSpanish));

      }

      const data = await response.json();

      if (!response.ok) {
        throw new Error(`Failed to ${isEditing ? 'update' : 'create'} agent`);
      }

      const agentData = data.data || data;

      if (isEditing) {
        dispatch({ type: 'UPDATE_AGENT', payload: agentData });
      } else {
        dispatch({ type: 'ADD_AGENT', payload: agentData });
      }

      setIsModalOpen(false);
      setFormData({
        nombre: '',
        nif: '',
        direccion: '',
        localidad: '',
        pais: '',
        eori: '',
      });

      fetchAgents();

    } catch (err) {
      console.error('Error submitting agent:', err);
      if (isEditing) {
        toast.error(getCustomsAgentManagementTranslation('errorUpdateAgent', isSpanish));
      } else {
        toast.error(getCustomsAgentManagementTranslation('errorCreateAgent', isSpanish));
      }
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgents = async () => {
    const userId = sessionStorage.getItem('user_id');
    if (!userId) {
      console.warn('No user_id found in sessionStorage, agent fetch might fail');
      return;
    }

    try {
      const response = await fetch(
        `https://${process.env.REACT_APP_DOCS_API_KEY}/api/items/agents?filter%5Buser_id%5D%5B_eq%5D=${userId}`,
        {
          headers: {
            'Authorization': `Bearer ${sessionStorage.getItem('token')}`,
            'Content-Type': 'application/json',
            'accept': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch agents');
      }

      const data = await response.json();

      const agentsData = data.data || (Array.isArray(data) ? data : []);

      dispatch({ type: 'SET_AGENTS', payload: agentsData });
    } catch (err) {
      console.error("Error fetching agents:", err.message);
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
  };

  // Find the city (localidad) with the most agents
  const cityCounts = agents.reduce((acc, agent) => {
    if (agent.localidad) {
      acc[agent.localidad] = (acc[agent.localidad] || 0) + 1;
    }
    return acc;
  }, {});
  let topCity = '-';
  let topCityCount = 0;
  for (const [city, count] of Object.entries(cityCounts)) {
    if (count > topCityCount) {
      topCity = city;
      topCityCount = count;
    }
  }
  let topCityDisplay = topCity;
  // Do not append the count, just show the city name

  return (
    <div className="text-left">
        <h1 className='text-3xl font-bold mb-20'>{getCustomsAgentManagementTranslation('customsAgentInformationManagement', isSpanish)}</h1>
         <div className="grid grid-cols-3 gap-4 mb-8 pb-8 border-bottom-gray">
        <CardStat value={agents.length} title={isSpanish ? 'Agentes' : 'Agents'} />
        <CardStat value={topCityDisplay} title={isSpanish ? 'Ciudad con más agentes' : 'Top Agent City'} />
      </div>
      <div className='flex justify-between gap-4 mb-4 items-center'>
        <h2 className='text-xl font-bold'>{isSpanish ? 'Lista de agentes' : 'Agent List'}</h2>
        <div className="flex gap-2 items-center">
           {/* Custom Sort Dropdown */}
           <div className="relative button-no-hover-effect">
            <button
              className="flex items-center gap-2 px-2 py-2 rounded-full truncate"
              onClick={() => setIsSortOpen(!isSortOpen)}
            >
              <ArrowUpDown className="w-4 h-4" />
              <span>{getSortLabel()}</span>
              <ChevronDown className={`w-4 h-4 transition-transform ${isSortOpen ? 'rotate-180' : ''}`} />
            </button>
            
            {isSortOpen && (
              <div className="overflow-hidden absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border-container py-1 z-10">
                {sortOptions.map((option) => (
                  <button
                    key={option.value}
                    className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                      sortBy === option.value ? 'text-primary' : 'text-gray-700'
                    }`}
                    onClick={() => {
                      setSortBy(option.value);
                      setIsSortOpen(false);
                    }}
                  >
                    {option.label}
                  </button>
                ))}
              </div>
            )}
          </div>
          {/* Search Input */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <Search className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              className="w-64 pl-10 pr-4 py-2 border-container rounded-full"
              placeholder={getCustomsAgentManagementTranslation('searchAgents', isSpanish)}
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

         
          <button
            onClick={handleAdd}
            className='border rounded-full py-2 px-4 pr-6 truncate flex gap-2 items-center bg-default text-white'
            disabled={isLoading}
          >
            <Plus className="size-5 stroke-white" />
            {getCustomsAgentManagementTranslation('addAgent', isSpanish)}
          </button>
        </div>
      </div>

      {/* Modal for Adding/Editing Agent */}
      {isModalOpen && (
        <AddorEditAgent handleSubmit={handleSubmit} handleChange={handleChange} isEditing={isEditing} closeModal={closeModal}
          formData={formData} isLoading={isLoading} />
      )}

      {/* Delete Confirmation Modal */}
      {isDeleteModalOpen && agentToDelete && (
        <DeleteAgent closeDeleteModal={closeDeleteModal} isLoading={isLoading} agentToDelete={agentToDelete} confirmDelete={confirmDelete} />
      )}

      <AgentsTable agents={sortedAgents} isLoading={isLoading} handleEdit={handleEdit} openDeleteModal={openDeleteModal} />
    </div>
  );
}

export default CustomsAgent;